/**
 * «Del i sosiale medier», phase 1 (owner, 2026-10-22): a published banner or announcement made ready to post.
 *
 * Opens under its row (not a dialog: the list stays readable beside it). Three things, in the order a person posts:
 *  1. the picture, in the size the app wants — drawn here from core's layout, previewed, downloaded;
 *  2. the caption for Facebook, Instagram or WhatsApp — built by core from what was published, editable, copied;
 *  3. «Del …» hands both to the phone's own share sheet (where the browser can share a file), and «Åpne i WhatsApp»
 *     opens WhatsApp with the text, so the person picks the group.
 * Nothing is posted from here and nobody's social-media login is held: the person posts from their own account.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Field, Msg, TwoStep } from './ui';
import { useMsg } from '../lib/hooks';
import { Social } from '../core';
import { blobBase64, captionParts, copyText, drawShareImage, INSTAGRAM_SIZES, mosquePublic, showsDirect, type MosquePublic, type ShareSource } from '../lib/social';
import { call, can, errText, post, saveBlob, type Body } from '../lib/api';
import { useNav } from '../lib/nav';

export function SharePanel({ source, title, onClose }: { source: ShareSource; title: string; onClose: () => void }) {
  const [mosque, setMosque] = useState<MosquePublic | null>(null);
  const [size, setSize] = useState(Social.SIZE_ORDER[0]);
  const [network, setNetwork] = useState(Social.NETWORK_ORDER[0]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [img, setImg] = useState<{ blob: Blob; url: string } | null>(null);
  const [drawing, setDrawing] = useState(true);
  const [msg, say] = useMsg();
  // Phase 2 (2026-10-23): straight to the mosque's own Page and Instagram account, where the mosque has it.
  const { caps, go } = useNav();
  // WhatsApp phase 1 (2026-10-24): where the mosque keeps its groups and the log — offered beside «Åpne i WhatsApp».
  const waPage = Boolean(caps && caps.includes('whatsapp') && (can('whatsapp.send') || can('whatsapp.manage')));
  const direct = showsDirect(caps, can);
  const [meta, setMeta] = useState<Body | null>(null);
  const [targets, setTargets] = useState<Record<string, boolean>>({ facebook: true, instagram: true });
  const [results, setResults] = useState<Body[] | null>(null);
  const [dmsg, dsay] = useMsg();

  useEffect(() => { let live = true; void mosquePublic().then((m) => { if (live) setMosque(m); }); return () => { live = false; }; }, []);
  useEffect(() => { if (direct) void call('/social').then((m: Body) => setMeta(m)).catch(() => setMeta(null)); }, [direct]);

  // The picture, again whenever the size changes; the old object URL is let go.
  useEffect(() => {
    if (!mosque) return;
    let live = true, made: string | null = null;
    setDrawing(true);
    void drawShareImage(source, size, mosque.shortName).then((blob) => {
      if (!live) return;
      setDrawing(false);
      if (!blob) { setImg(null); say('Bildet kunne ikke lages i denne nettleseren. Teksten kan fortsatt kopieres.', 'err'); return; }
      made = URL.createObjectURL(blob); setImg({ blob, url: made });
    });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [mosque, size, source.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const parts = useMemo(() => captionParts(source), [source]);
  const built = useMemo(() => Social.caption({ ...parts, mosque: mosque || {}, network }), [parts, mosque, network]);
  const text = edits[network] ?? built.text;
  const net = Social.NETWORKS[network];
  const over = text.length > net.max;
  const file = img ? Social.fileName(title, size, source.kind === 'notice' ? 'kunngjoring' : 'banner') : '';
  const shareFile = img ? new File([img.blob], file, { type: 'image/jpeg' }) : null;
  // The share sheet only where the browser can hand over a FILE (phones, Safari, Edge); otherwise the button is not shown.
  const canShareFile = Boolean(shareFile && typeof navigator.canShare === 'function' && navigator.canShare({ files: [shareFile] }));

  async function copy() {
    const ok = await copyText(text);
    say(ok ? `Teksten for ${net.name_no} er kopiert. Lim den inn under bildet.` : 'Teksten kunne ikke kopieres. Merk den og kopier selv.', ok ? 'ok' : 'err');
  }
  const captionFor = (n: string) => edits[n] ?? Social.caption({ ...parts, mosque: mosque || {}, network: n }).text;
  const igOk = INSTAGRAM_SIZES.includes(size);
  const chosen = ['facebook', 'instagram'].filter((n) => targets[n] && meta && meta[n] && (n !== 'instagram' || igOk));
  async function publishDirect() {
    if (!img || !chosen.length) return;
    setResults(null); dsay('');
    const image = await blobBase64(img.blob);
    const r = await post('/social/publish', { kind: source.kind, id: source.id, size, image, networks: chosen,
      captions: Object.fromEntries(chosen.map((n) => [n, captionFor(n)])) });
    if (!r.ok) return dsay(errText(r.body), 'err');
    setResults(r.body.results || []);
    const failed = (r.body.results || []).filter((x: Body) => x.status !== 'sent');
    dsay(failed.length ? failed.map((x: Body) => x.message_no).join(' ') : 'Publisert.', failed.length ? 'err' : 'ok');
    if (failed.some((x: Body) => x.code === 'reconnect')) void call('/social').then((m: Body) => setMeta(m)).catch(() => {});
  }

  async function share() {
    if (!shareFile) return;
    try { await navigator.share({ files: [shareFile], text, title }); say('Sendt til delingsmenyen.', 'ok'); }
    catch (e) { if (!(e instanceof DOMException && e.name === 'AbortError')) say('Delingen ble ikke fullført. Last ned bildet og kopier teksten i stedet.', 'err'); }
  }

  return (
    <div className="share" role="region" aria-label={`Del «${title}» i sosiale medier`}>
      <div className="share-head">
        <b>Del i sosiale medier</b>
        <span className="sub">Du legger ut fra moskeens egen konto. Ingenting publiseres herfra.</span>
        <span style={{ flex: 1 }} />
        <Btn ghost onClick={onClose} aria-label={`Lukk deling av «${title}»`}>Lukk</Btn>
      </div>
      <div className="share-body">
        <div className="share-pic">
          <div className="bnr-seg" role="radiogroup" aria-label="Bildestørrelse">
            {Social.SIZE_ORDER.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={size === k} title={Social.SIZES[k].for_no} onClick={() => setSize(k)}>
                {Social.SIZES[k].label_no.split(' ')[0]}
              </button>))}
          </div>
          <p className="field note" style={{ margin: '0 0 8px' }}>{Social.SIZES[size].label_no} · {Social.SIZES[size].for_no}</p>
          <div className={'share-frame ' + size} aria-busy={drawing || undefined}>
            {img ? <img src={img.url} alt={`Bildet som deles: ${title}`} /> : <span className="sub">{drawing ? 'Lager bildet …' : 'Ingen forhåndsvisning'}</span>}
          </div>
          <div className="row">
            <Btn disabled={!img} onClick={() => img && saveBlob(img.blob, file)}>Last ned bilde</Btn>
            {canShareFile && <Btn ghost onClick={share}>Del …</Btn>}
          </div>
        </div>
        <div className="share-text">
          <div className="bnr-seg" role="radiogroup" aria-label="Tekst for">
            {Social.NETWORK_ORDER.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={network === k} onClick={() => setNetwork(k)}>{Social.NETWORKS[k].name_no}</button>))}
          </div>
          <Field label={<>Tekst til {net.name_no}<span className={'count' + (over ? ' over' : '')} aria-live="polite">{text.length} / {net.max}</span></>}
                 note={network === 'instagram' ? 'Instagram gjør ikke lenker i teksten klikkbare, så nettsiden står som tekst. Høyst 2 200 tegn.'
                   : network === 'whatsapp' ? 'Stjernene gjør tittelen fet i WhatsApp. Du velger selv gruppen når WhatsApp åpnes.'
                   : 'Lenken til nettsiden blir klikkbar på Facebook.'}>
            {(id, d) => <textarea id={id} aria-describedby={d} rows={8} value={text} onChange={(e) => setEdits({ ...edits, [network]: e.target.value })} />}
          </Field>
          {built.truncated && edits[network] === undefined && <p className="field note warn">Teksten er kortet ned til det {net.name_no} tar imot.</p>}
          <div className="row">
            <Btn onClick={copy} disabled={over}>Kopier tekst</Btn>
            {network === 'whatsapp' && <a className="btn sm ghost" href={Social.whatsappLink(text)} target="_blank" rel="noopener noreferrer">Åpne i WhatsApp</a>}
            {network === 'whatsapp' && waPage && <Btn ghost onClick={() => go('whatsapp')}>Moskeens grupper og logg</Btn>}
            {edits[network] !== undefined && <Btn ghost onClick={() => { const n = { ...edits }; delete n[network]; setEdits(n); }}>Tilbakestill tekst</Btn>}
          </div>
        </div>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
      {direct && meta && (
        <div className="share-direct" role="group" aria-label="Publiser rett på moskeens sider">
          <b>Publiser rett på moskeens sider</b>
          {!meta.connected ? <p className="field note">Ikke koblet til. En administrator kobler til Facebook og Instagram under Nettside.</p> : <>
            <div className="row" style={{ alignItems: 'center' }}>
              <label className="chk"><input type="checkbox" checked={targets.facebook} onChange={(e) => setTargets({ ...targets, facebook: e.target.checked })} /> Facebook ({meta.facebook.name})</label>
              {meta.instagram
                ? <label className="chk" title={igOk ? undefined : 'Instagram tar ikke imot story-format i et innlegg'}><input type="checkbox" disabled={!igOk} checked={igOk && targets.instagram}
                    onChange={(e) => setTargets({ ...targets, instagram: e.target.checked })} /> Instagram ({meta.instagram.name}){igOk ? '' : ' — velg kvadrat eller stående'}</label>
                : <span className="sub">Ingen Instagram-konto er koblet til siden.</span>}
            </div>
            <p className="field note" style={{ margin: '4px 0 8px' }}>Bildet over og teksten for hvert nettverk legges ut slik de står. Du som skrev det, publiserer — ingen andre må godkjenne.</p>
            <div className="row">
              <TwoStep ghost={false} go disabled={!img || !chosen.length} label="Publiser" busyLabel="Publiserer …"
                ariaLabel={`Publiser «${title}» på ${chosen.map((n) => (n === 'facebook' ? 'Facebook' : 'Instagram')).join(' og ') || 'Facebook og Instagram'}`}
                confirmLabel={`Klikk igjen: legges ut på ${chosen.map((n) => (n === 'facebook' ? 'Facebook' : 'Instagram')).join(' og ')} nå`} onConfirm={publishDirect} />
            </div>
            {results && <ul className="share-results">{results.map((x) => (
              <li key={x.network}>{x.network === 'facebook' ? 'Facebook' : 'Instagram'}: {x.status === 'sent'
                ? (x.permalink ? <a href={x.permalink} target="_blank" rel="noopener noreferrer">{x.already ? 'allerede lagt ut — se innlegget' : 'lagt ut — se innlegget'}</a> : 'lagt ut')
                : <span className="err">{x.message_no}</span>}</li>))}</ul>}
          </>}
          <Msg text={dmsg.text} tone={dmsg.tone} />
        </div>
      )}
    </div>
  );
}
